import React, { useCallback } from 'react';
import { Folder, MessageCircle } from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import { Text } from '@cotera/client/app/components/ui';
import { DG } from '@cotera/sdk/core';
import type { StoredMessage } from '@cotera/api';
import { useWhoami } from '@cotera/client/app/stores/org';
import {
  ResourceListView,
  ResourceListSecondaryCells,
  type FilterOption,
  type SortOption,
  type ItemAction,
} from '../../resources/list/resource-list-view';
import { ResourceScopeBadge } from '../../resources/list/resource-scope-badge';
import { ResourceCreatorCell } from '../../resources/list/resource-creator-cell';
import {
  RESOURCE_LIST_CELL_TEXT,
  RESOURCE_LIST_COLUMNS,
} from '../../resources/list/resource-list-types';
import type { FolderScope } from '../../resources/list/resource-list-types';
import type { ConversationListItem } from '../models/chat-history-list-page.model';
import { formatChatTriggerSource } from '../utils/chat-trigger-source';
import type { Column } from '../../resources/list/resource-list-view-types';

const CHAT_TRIGGER_COLUMN: Column = {
  label: 'Trigger',
  width: '120px',
};

/** Leading columns both layouts share; only the trigger column differs. */
const CHAT_LEADING_COLUMNS: Column[] = [
  // Named in the column table, alongside Created by / Shared with / Last
  // active — one unlabelled column among labelled ones reads as a header that
  // failed, not as restraint. Silent in the stacked header, where it would be
  // the only word on screen and every row is already a chat title beside a chat
  // icon. See `Column.labelHidden`.
  { columnKey: 'name', label: 'Name', labelHidden: true },
  RESOURCE_LIST_COLUMNS.createdBy,
  RESOURCE_LIST_COLUMNS.sharedWith,
];

export const CHAT_COLUMNS: Column[] = [
  ...CHAT_LEADING_COLUMNS,
  RESOURCE_LIST_COLUMNS.lastActive,
];

export const AGENT_CHAT_COLUMNS: Column[] = [
  ...CHAT_LEADING_COLUMNS,
  CHAT_TRIGGER_COLUMN,
  RESOURCE_LIST_COLUMNS.lastActive,
];

export type ChatListColumnLayout = 'default' | 'agent';

const WORKFLOW_TITLE_UTC_SUFFIX =
  /^(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{1,2}(?:, \d{4})?, \d{2}:\d{2} UTC$/;
const WORKFLOW_TITLE_UTC_TIME_ONLY_SUFFIX = /^\d{2}:\d{2} UTC$/;

/** Workflow runs append ` · MMM D, HH:MM UTC` to stored chat titles — hide that in lists. */
export const stripWorkflowTitleTimestamp = (title: string): string => {
  const separator = ' · ';
  const separatorIndex = title.lastIndexOf(separator);
  if (separatorIndex === -1) {
    return title;
  }
  const suffix = title.slice(separatorIndex + separator.length);
  if (
    WORKFLOW_TITLE_UTC_SUFFIX.test(suffix) ||
    WORKFLOW_TITLE_UTC_TIME_ONLY_SUFFIX.test(suffix)
  ) {
    return title.slice(0, separatorIndex).trim();
  }
  return title;
};

export const getDisplayTitle = (
  title: string | null | undefined,
  opts?: {
    firstMessage?: StoredMessage | null;
    lastMessage?: StoredMessage | null;
  }
): string => {
  if (title !== undefined && title !== null && title.trim() !== '') {
    return stripWorkflowTitleTimestamp(title.trim());
  }
  const fromFirst = getMessageText(opts?.firstMessage ?? null);
  if (fromFirst !== null) {
    return fromFirst;
  }
  const fromLast = getMessageText(opts?.lastMessage ?? null);
  if (fromLast !== null) {
    return fromLast;
  }
  return 'Untitled';
};

export const getMessageText = (msg: StoredMessage | null): string | null => {
  if (msg === null) {
    return null;
  }

  const raw = msg.message;
  let body: { parts?: unknown[]; content?: string } | null = null;
  if (raw === null || raw === undefined) {
    return null;
  }
  if (typeof raw === 'string') {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed === 'object' && parsed !== null) {
        body = parsed as { parts?: unknown[]; content?: string };
      } else {
        const trimmed = raw.trim();
        return trimmed !== ''
          ? trimmed.length > 120
            ? trimmed.slice(0, 120) + '...'
            : trimmed
          : null;
      }
    } catch {
      const trimmed = raw.trim();
      return trimmed !== ''
        ? trimmed.length > 120
          ? trimmed.slice(0, 120) + '...'
          : trimmed
        : null;
    }
  } else if (typeof raw === 'object') {
    body = raw as { parts?: unknown[]; content?: string };
  }

  if (body === null) {
    return null;
  }

  if (typeof body.content === 'string' && body.content.trim() !== '') {
    const trimmed = body.content.trim();
    return trimmed.length > 120 ? trimmed.slice(0, 120) + '...' : trimmed;
  }

  if (body.parts === undefined || !Array.isArray(body.parts)) {
    return null;
  }

  for (const part of body.parts) {
    const p = part as { type?: string; text?: string; editorState?: unknown };
    if (typeof p.text === 'string' && p.text.trim() !== '') {
      const trimmed = p.text.trim();
      return trimmed.length > 120 ? trimmed.slice(0, 120) + '...' : trimmed;
    }
    if (p.type === 'editor-state' && p.editorState !== undefined) {
      try {
        const editor = new DG.DocumentEditor.EditorState(
          p.editorState as DG.DocumentEditor.DocumentState
        );
        const text = editor
          .toPromptMarkdown(DG.BuildExplorer.empty(), 'simple')
          .trim();
        if (text !== '') {
          return text.length > 120 ? text.slice(0, 120) + '...' : text;
        }
      } catch {
        return null;
      }
    }
  }
  return null;
};

const isSameCalendarDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
};

/** Last-active label: time only when today, otherwise short date + time. */
export const formatChatLastActive = (
  date: Date,
  now: Date = new Date()
): string => {
  if (isSameCalendarDay(date, now)) {
    return date.toLocaleTimeString(undefined, TIME_FORMAT);
  }
  const includeYear = date.getFullYear() !== now.getFullYear();
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(includeYear ? { year: 'numeric' as const } : {}),
    ...TIME_FORMAT,
  });
};

export const filterConversation = (
  conversation: ConversationListItem,
  query: string
): boolean => {
  const title = getDisplayTitle(conversation.title, {
    firstMessage: conversation.firstMessage,
    lastMessage: conversation.lastMessage,
  }).toLowerCase();
  return title.includes(query);
};

export const renderConversationIcon = (
  conversation: ConversationListItem
): React.ReactNode => {
  const unread = conversation.hasUnreadAssistant === true;
  return (
    <span className="relative inline-flex h-5 w-5 items-center justify-center">
      <MessageCircle
        className={cn(
          'h-5 w-5',
          unread ? 'text-primary' : 'text-muted-foreground'
        )}
      />
      {unread ? (
        <span
          className="absolute top-0 right-0 h-2 w-2 rounded-full bg-primary ring-2 ring-background group-hover:ring-secondary group-data-[selected=true]:ring-secondary"
          aria-label="Unread assistant message"
        />
      ) : null}
    </span>
  );
};

/**
 * Which line a row shows under its title.
 *
 * The folder wins over the snippet where both are available: knowing where a
 * chat lives places it, which a snippet can't. But a list already scoped to one
 * folder has nothing to place — the breadcrumb names the folder and every row
 * is in it — so there the folder is suppressed and the snippet takes the line
 * back.
 *
 * Pure so the precedence can be tested without rendering a row.
 */
export function chatRowSubheading(params: {
  folderName: string | null | undefined;
  /** The list is scoped to one folder, so naming it per row says nothing new. */
  hideFolderName: boolean;
  showPreview: boolean;
}): 'folder' | 'preview' | 'none' {
  const { folderName, hideFolderName, showPreview } = params;
  const hasFolder =
    !hideFolderName &&
    folderName !== undefined &&
    folderName !== null &&
    folderName !== '';
  if (hasFolder) {
    return 'folder';
  }
  return showPreview ? 'preview' : 'none';
}

/**
 * Render the text cells for a single conversation row. The leading icon and
 * `<tr>` wrapper are managed by `ResourceListView`.
 */
export const renderConversationRow = (
  conversation: ConversationListItem,
  opts?: {
    showPreview?: boolean;
    columnLayout?: ChatListColumnLayout;
    compact?: boolean;
    /** The viewer, so their own chats read as "You" in the creator column. */
    currentUserId?: string;
    /**
     * Drop the folder label from the subheading. For a list already scoped to
     * one folder, where naming it on every row only repeats the breadcrumb.
     */
    hideFolderName?: boolean;
  }
): React.ReactNode => {
  const columnLayout = opts?.columnLayout ?? 'default';
  const compact = opts?.compact === true;
  const title = getDisplayTitle(conversation.title, {
    firstMessage: conversation.firstMessage,
    lastMessage: conversation.lastMessage,
  });
  const showPreview = opts?.showPreview === true;
  const subheadingKind = chatRowSubheading({
    folderName: conversation.folderName,
    hideFolderName: opts?.hideFolderName === true,
    showPreview,
  });
  const preview =
    subheadingKind === 'preview'
      ? getMessageText(conversation.lastMessage) ?? 'No messages'
      : '';
  const lastActive = formatChatLastActive(new Date(conversation.updatedAt));
  const lastActiveTitle = new Date(conversation.updatedAt).toLocaleString(
    undefined,
    {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    }
  );
  const triggerLabel = formatChatTriggerSource(conversation.triggerSource);
  const currentUserId = opts?.currentUserId;
  const subheading =
    subheadingKind === 'folder' ? (
      <span className="inline-flex min-w-0 items-center gap-1">
        <Folder className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span className="truncate">{conversation.folderName}</span>
      </span>
    ) : subheadingKind === 'preview' ? (
      <span className="truncate">{preview}</span>
    ) : null;
  // Purely "who can see this" now — inbound shares no longer trade their scope
  // away to name their owner, because the creator column names them instead.
  const scopeCell = (
    <ResourceScopeBadge
      scope={(conversation.folderScope ?? 'user') as FolderScope}
    />
  );
  // `ResourceCreatorCell` drops the "Owned by" prefix the badge carried: the
  // column header already says whose name this is. It needs a viewer to read
  // "You" against, so it only renders where one was passed.
  const creatorCell =
    currentUserId === undefined ? null : (
      <ResourceCreatorCell
        creatorId={conversation.actorId}
        currentUserId={currentUserId}
      />
    );

  if (compact) {
    return (
      <div className="min-w-0 w-full">
        <Text.P className="font-medium text-slate-800 min-w-0 truncate">
          {title}
        </Text.P>
        {subheading !== null ? (
          <p className="text-[11px] text-muted-foreground truncate mt-0.5 flex min-w-0 items-center gap-1">
            {subheading}
          </p>
        ) : null}
        <div className="flex items-center gap-x-3 gap-y-1 mt-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0 flex-1">
            {creatorCell}
            {scopeCell}
            {columnLayout === 'agent' ? (
              <span className={RESOURCE_LIST_CELL_TEXT}>{triggerLabel}</span>
            ) : null}
          </div>
          <div title={lastActiveTitle} className="shrink-0 text-right">
            <span className={cn(RESOURCE_LIST_CELL_TEXT, 'tabular-nums')}>
              {lastActive}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-w-0 w-full @list-cols/list:flex-1 @list-cols/list:min-w-0">
        <div className="min-w-0">
          <Text.P className="font-medium text-slate-800 min-w-0 truncate">
            {title}
          </Text.P>
          {subheading !== null ? (
            <p className="text-[11px] text-muted-foreground truncate mt-0.5 flex min-w-0 items-center gap-1">
              {subheading}
            </p>
          ) : null}
        </div>
      </div>
      <ResourceListSecondaryCells>
        {/* `max-w-full` for the stacked layout, where this cell is sized by its
            content and cannot shrink — see the matching cell in `LibraryRow`. */}
        <div className="min-w-0 max-w-full shrink-0 text-left @list-cols/list:w-[140px] @list-cols/list:pl-3">
          {creatorCell}
        </div>
        <div className="shrink-0 text-left @list-cols/list:w-[150px] @list-cols/list:pl-3">
          <div className="flex">{scopeCell}</div>
        </div>
        {columnLayout === 'agent' ? (
          <div className="shrink-0 text-left @list-cols/list:w-[120px] @list-cols/list:pl-3 min-w-0">
            <span className={cn(RESOURCE_LIST_CELL_TEXT, 'block truncate')}>
              {triggerLabel}
            </span>
          </div>
        ) : null}
        <div
          title={lastActiveTitle}
          className="shrink-0 text-left @list-cols/list:w-[140px] @list-cols/list:pl-3"
        >
          <span className={cn(RESOURCE_LIST_CELL_TEXT, 'block w-full')}>
            {lastActive}
          </span>
        </div>
      </ResourceListSecondaryCells>
    </>
  );
};

export type ChatListViewProps = {
  items: ConversationListItem[];
  status: 'loading' | 'ready';
  /** Total across all pages; falls back to loaded count when omitted. */
  totalCount?: number;
  onClickConversation: (conversation: ConversationListItem) => void;
  /** Controlled search query, synced by the caller. */
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  /** Show the message-preview line under each title. */
  showPreview?: boolean;
  /**
   * Suppress each row's folder label. Pass for a list scoped to a single folder
   * (the folder's chat history), where the breadcrumb already names it and every
   * row would otherwise repeat it; rows then show their message snippet instead.
   */
  hideFolderName?: boolean;
  /** Column layout; `agent` adds a trigger column alongside folder and scope. */
  columnLayout?: ChatListColumnLayout;
  layout?: 'page' | 'embedded';
  /** Drop the "Chats" heading and keep only the count. See `ResourceListView`. */
  hideTitle?: boolean;
  /**
   * Header scale. `compact` is the standalone-page size the Library and
   * Automations use — a `text-2xl` serif title with tighter top spacing.
   */
  headerSize?: 'default' | 'compact';
  /**
   * Replaces `ResourceListView`'s default `px-8`. Pass `px-0` when the host page
   * already owns the page gutters, so the two don't stack.
   */
  containerClassName?: string;
  // Pagination (cumulative `loadPage` style, matching the chat list model).
  hasMore?: boolean;
  loadPage?: (page: number, take: number) => void;
  page?: number;
  take?: number;
  isLoadingMore?: boolean;
  // Optional toolbar / row features (omitted for minimal embedded lists).
  sortOptions?: SortOption<ConversationListItem>[];
  activeSort?: string;
  sortDirection?: 'asc' | 'desc';
  onSortChange?: (key: string, direction: 'asc' | 'desc') => void;
  filterOptions?: FilterOption[];
  itemActions?: (conversation: ConversationListItem) => ItemAction[];
  onFocus?: (conversations: ConversationListItem[]) => void;
  onOpenActionMenu?: (conversation: ConversationListItem) => void;
  primaryAction?: { label: string; onClick: () => void };
  // Copy overrides (defaults match the standalone chat history page).
  emptyText?: string;
  noMatchText?: string;
  searchPlaceholder?: string;
  columns?: Column[];
};

/**
 * Presentational chat list built on `ResourceListView`. Owns no data, URL, or
 * model state — callers pass items, controlled search, paging, and the feature
 * set they want. Shared by the chat history page and the agent Chats tab.
 */
export const ChatListView: React.FC<ChatListViewProps> = ({
  items,
  status,
  totalCount,
  onClickConversation,
  searchQuery,
  onSearchQueryChange,
  showPreview = false,
  hideFolderName = false,
  columnLayout = 'default',
  hideTitle,
  layout = 'page',
  headerSize,
  containerClassName,
  hasMore,
  loadPage,
  page,
  take,
  isLoadingMore = false,
  sortOptions,
  activeSort,
  sortDirection,
  onSortChange,
  filterOptions,
  itemActions,
  onFocus,
  onOpenActionMenu,
  primaryAction,
  emptyText = 'No conversations yet',
  noMatchText = 'No conversations match your search',
  searchPlaceholder = 'Search...',
  columns = CHAT_COLUMNS,
}) => {
  const currentUserId = useWhoami((s) => s.whoami.id);
  const renderItem = useCallback(
    (conversation: ConversationListItem) =>
      renderConversationRow(conversation, {
        showPreview,
        hideFolderName,
        columnLayout,
        compact: layout === 'embedded',
        currentUserId,
      }),
    [showPreview, hideFolderName, columnLayout, layout, currentUserId]
  );

  return (
    <ResourceListView
      layout={layout}
      hideTitle={hideTitle}
      headerSize={headerSize}
      containerClassName={containerClassName}
      // The ⋯ menu gets its own trailing column, as it does on the Library and
      // Automations rows. Without it the menu overlays the leading chat icon and
      // fades it out on hover — so the icon (and its unread dot) disappeared
      // whenever the pointer crossed a row on the way to clicking it.
      trailingActionsColumn
      title="Chats"
      noun="chat"
      items={items}
      totalCount={totalCount}
      status={status}
      filterFn={filterConversation}
      renderItem={renderItem}
      icon={MessageCircle}
      renderIcon={renderConversationIcon}
      filterOptions={filterOptions}
      keyFn={(c) => c.id}
      onClickItem={onClickConversation}
      onFocus={onFocus}
      itemActions={itemActions}
      onOpenActionMenu={onOpenActionMenu}
      emptyIcon={
        <MessageCircle className="h-12 w-12 text-muted-foreground/50 mb-4" />
      }
      emptyText={emptyText}
      noMatchText={noMatchText}
      primaryAction={primaryAction}
      searchPlaceholder={searchPlaceholder}
      searchQuery={searchQuery}
      onSearchQueryChange={onSearchQueryChange}
      sortOptions={sortOptions}
      activeSort={activeSort}
      sortDirection={sortDirection}
      onSortChange={onSortChange}
      columns={columns}
      hasMore={hasMore}
      loadPage={loadPage}
      page={page}
      take={take}
      isLoadingMore={isLoadingMore}
    />
  );
};
